import { useState, useEffect } from 'react';
import { DisplayNameScope } from './useUnifiedProfile';

/**
 * What the person picked in the editor. `reset` is not a scope of its own — it
 * puts the account name back on this one event.
 */
export type DisplayNameChoice = DisplayNameScope | 'reset';

interface UseDisplayNameEditorProps {
    initialDisplayName: string;
    onSave: (newName: string, scope: DisplayNameScope) => Promise<void>;
    /** The account-level name, offered as the "back to my account name" choice */
    accountDisplayName?: string;
    /** False when there is nothing to choose — a guest, or the account profile itself */
    canChooseScope?: boolean;
    defaultChoice?: DisplayNameChoice;
}

export const useDisplayNameEditor = ({
    initialDisplayName,
    onSave,
    accountDisplayName = '',
    canChooseScope = false,
    defaultChoice = 'event'
}: UseDisplayNameEditorProps) => {
    const [isEditing, setIsEditing] = useState(false);
    const [newDisplayName, setNewDisplayName] = useState('');
    const [error, setError] = useState('');
    const [choice, setChoice] = useState<DisplayNameChoice>(defaultChoice);
    const [saving, setSaving] = useState(false);

    // Saving "everywhere" writes the account name first and the event name last,
    // so mid-save the two disagree and the "back to my account name" choice would
    // appear under the user's cursor and vanish again. Hold the choices steady for
    // exactly as long as that request is in flight — freezing them for the whole
    // edit instead would miss an account name that only loads a moment later.
    const [inFlight, setInFlight] = useState<{ accountName: string; canReset: boolean } | null>(null);

    useEffect(() => {
        setNewDisplayName(initialDisplayName || '');
    }, [initialDisplayName]);

    // Nothing to reset to when the event already carries the account name
    const liveCanReset = canChooseScope
        && Boolean(accountDisplayName)
        && accountDisplayName !== initialDisplayName;

    const activeAccountName = inFlight?.accountName ?? accountDisplayName;
    const canReset = inFlight ? inFlight.canReset : liveCanReset;

    const handleEdit = () => {
        setIsEditing(true);
        setChoice(defaultChoice);
        setError('');
    };

    const handleSave = async () => {
        const isReset = choice === 'reset';
        const nameToSave = isReset ? activeAccountName.trim() : newDisplayName.trim();

        if (!nameToSave) {
            setError('Display name cannot be empty');
            return;
        }

        if (nameToSave.length < 2) {
            setError('Display name must be at least 2 characters');
            return;
        }

        if (nameToSave.length > 50) {
            setError('Display name must be less than 50 characters');
            return;
        }

        setSaving(true);
        setInFlight({ accountName: activeAccountName, canReset });
        try {
            await onSave(nameToSave, isReset ? 'event' : choice);
            setIsEditing(false);
            setError('');
        } catch (error) {
            setError('Failed to update display name');
            console.error('Error updating display name:', error);
        } finally {
            setSaving(false);
            setInFlight(null);
        }
    };

    const handleCancel = () => {
        setIsEditing(false);
        setNewDisplayName(initialDisplayName || '');
        setChoice(defaultChoice);
        setError('');
    };

    const handleKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            handleSave();
        } else if (e.key === 'Escape') {
            handleCancel();
        }
    };

    const handleChange = (value: string) => {
        setNewDisplayName(value);
    };

    return {
        isEditing,
        newDisplayName,
        error,
        saving,
        choice,
        setChoice,
        canChooseScope,
        accountDisplayName: activeAccountName,
        canReset,
        handleEdit,
        handleSave,
        handleCancel,
        handleKeyPress,
        handleChange
    };
};
